import React, { useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../ui/button.jsx';
import { databases, DATABASE_ID } from '../../services/appwrite';
import { restoreRecord } from '../../services/records';
import { canUpdate, isDeleted, isSoftDeleteTable } from '../../lib/records';
import { useAuth } from '../../context/AuthContext';

/**
 * Restore, offered on a "sent to the recycle bin" line in a record's history —
 * only while the record is still deleted, and only to someone who can edit it.
 *
 * It looks at the record itself, not at history: a later restore that history
 * has not caught up with yet, or a purge, must not leave a dead button.
 */
export default function RestoreFromHistory({ table, rowId, onRestored }) {
  const { user } = useAuth();
  const [row, setRow] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isSoftDeleteTable(table) || !rowId) return undefined;
    let cancelled = false;
    databases.getDocument(DATABASE_ID, table, rowId)
      .then((doc) => { if (!cancelled) setRow(doc); })
      .catch(() => { if (!cancelled) setRow(null); }); // purged, or not ours to read
    return () => { cancelled = true; };
  }, [table, rowId]);

  if (!row || !isDeleted(row) || !canUpdate(row, user)) return null;

  const handleRestore = async () => {
    setBusy(true);
    try {
      const restored = await restoreRecord(table, row);
      setRow(restored);
      toast.success('Restored');
      onRestored?.(table, restored);
    } catch (err) {
      toast.error(err?.message || 'Could not restore this');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button type="button" variant="outline" size="sm" onClick={handleRestore} disabled={busy}>
      <RotateCcw />
      {busy ? 'Restoring…' : 'Restore'}
    </Button>
  );
}
